import { useQuery } from '@tanstack/react-query';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import type { Me } from '../../lib/auth';
import { can } from '../../lib/permissions';

// Datos de /api/usuarios (guía 7.5): altas, bajas, directorio y portal de autoservicio de RR. HH.

export type PeopleStatus = 'pendiente' | 'aprobada' | 'rechazada' | 'cancelada';
export const PEOPLE_STATUSES: readonly PeopleStatus[] = ['pendiente', 'aprobada', 'rechazada', 'cancelada'];
export type PeopleScope = 'propias' | 'por_aprobar' | 'todas';
export type Billing = 'mensual' | 'anual';

export interface HireListItem {
  id: string;
  number: number;
  code: string;
  fullName: string;
  email: string;
  organizationId: string;
  organization: string | null;
  startDate: string;
  status: PeopleStatus;
  requester: string | null;
  manager: string | null;
  createdAt: string;
}

export interface SeparationListItem {
  id: string;
  number: number;
  code: string;
  userId: string;
  fullName: string | null;
  organizationId: string;
  organization: string | null;
  lastDay: string;
  status: PeopleStatus;
  applied: boolean;
  requester: string | null;
  createdAt: string;
}

export interface CostLine {
  licenseTypeId: string;
  name: string;
  billing: Billing;
  monthly: number;
  upfront: number;
  reason: 'compartida' | 'mensual' | 'puesto_libre' | 'prorrateo' | 'anual_completo';
}

export interface CostEstimate {
  lines: CostLine[];
  monthly: number;
  upfront: number;
}

export interface OriginTask {
  id: string;
  title: string;
  status: string;
  statusCategory: 'por_hacer' | 'en_curso' | 'terminado' | 'cancelado';
  dueDate: string | null;
  assignees: string[];
}

interface Decision {
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
}

export interface HireDetail extends Decision {
  id: string;
  number: number;
  code: string;
  status: PeopleStatus;
  organizationId: string;
  organization: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  department: string | null;
  position: string | null;
  managerId: string;
  manager: string | null;
  startDate: string;
  notes: string | null;
  requesterId: string;
  requester: string | null;
  licenses: Array<{ id: string; name: string; billing: Billing }>;
  estimate: CostEstimate;
  createdUserId: string | null;
  tasks: OriginTask[];
  createdAt: string;
  can: { decide: boolean; cancel: boolean };
}

export interface SeparationDetail extends Decision {
  id: string;
  number: number;
  code: string;
  status: PeopleStatus;
  organizationId: string;
  organization: string | null;
  userId: string;
  fullName: string | null;
  email: string | null;
  department: string | null;
  position: string | null;
  manager: string | null;
  userStatus: string | null;
  lastDay: string;
  reason: string | null;
  requesterId: string;
  requester: string | null;
  appliedAt: string | null;
  licenses: Array<{ id: string; name: string; billing: Billing; startsOn: string }>;
  pending: { reports: number; tickets: number; tasks: number; projects: number };
  tasks: OriginTask[];
  createdAt: string;
  can: { decide: boolean; cancel: boolean };
}

export interface FormLicense {
  id: string;
  name: string;
  billing: Billing;
  baseCost: number;
  shared: boolean;
  renewalDate: string | null;
  seatsPurchased: number | null;
  assigned: number;
}

export interface SeparablePerson {
  id: string;
  fullName: string;
  email: string;
  department: string | null;
  position: string | null;
  licenses: string[];
}

export interface PeopleFormData {
  organizationId: string;
  organizations: Array<{ id: string; name: string; groupName: string }>;
  departments: Array<{ id: string; name: string }>;
  positions: Array<{ id: string; name: string; area: string | null }>;
  licenses: FormLicense[];
  managers: Array<{ id: string; fullName: string }>;
  separable: SeparablePerson[];
  isHr: boolean;
}

export interface DirectoryUser {
  id: string;
  fullName: string;
  email: string;
  status: 'activo' | 'inactivo' | 'baja';
  organizationId: string;
  organization: string | null;
  department: string | null;
  position: string | null;
  managerId: string | null;
  manager: string | null;
  licenses: number;
}

export interface PersonLicense {
  id: string;
  licenseTypeId: string;
  name: string;
  billing: Billing;
  startsOn: string;
  endsOn: string | null;
  source: string;
  note: string | null;
  canRelease: boolean;
}

export interface PersonHistoryEntry {
  id: string;
  action: string;
  actor: string | null;
  at: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

/** Ficha de una persona (GET /api/usuarios/:id). */
export interface PersonDetail {
  id: string;
  fullName: string;
  email: string;
  status: DirectoryUser['status'];
  isVp: boolean;
  organizationId: string;
  organization: string | null;
  departmentId: string | null;
  department: string | null;
  positionId: string | null;
  position: string | null;
  managerId: string | null;
  manager: string | null;
  createdAt: string;
  licenses: PersonLicense[];
  assignableLicenses: Array<{ id: string; name: string; billing: Billing }>;
  history: PersonHistoryEntry[];
  can: { edit: boolean; request: boolean; assignLicenses: boolean };
}

export interface TeamMember {
  id: string;
  fullName: string;
  email: string;
  status: DirectoryUser['status'];
  organization: string;
  department: string | null;
  position: string | null;
}

/** Qué puede pedir cambiar el jefe directo (POST /api/usuarios/:id/cambio). */
export const CHANGE_KINDS = ['jefe', 'puesto', 'organizacion', 'licencias', 'datos'] as const;

export const usePeopleForm = (organizationId?: string) =>
  useQuery({
    queryKey: ['usuarios', 'formulario', organizationId ?? 'propia'],
    queryFn: () => api<PeopleFormData>(`/api/usuarios/formulario${organizationId ? `?organizacion=${organizationId}` : ''}`),
    placeholderData: (prev) => prev,
  });

/** RR. HH. (aprueba) o IT (gestiona): ve el portal de autoservicio y el directorio. */
export const isHr = (me: Me, organizationId?: string) => can(me, 'usuarios.aprobar', organizationId) || can(me, 'usuarios.gestionar', organizationId);
export const canSeeAll = (me: Me) => can(me, 'usuarios.ver') || isHr(me);

export const statusName = (s: PeopleStatus) => t(`us.estado_${s}` as MessageKey);
const TONES = { pendiente: 'amber', aprobada: 'blue', rechazada: 'red', cancelada: 'slate' } as const;
export const statusTone = (s: PeopleStatus) => TONES[s];

export const formatDate = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('es-PA', { day: 'numeric', month: 'short', year: 'numeric' });
export const formatMoney = (n: number) => `USD ${n.toLocaleString('es-PA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Hoy en la zona del navegador (AAAA-MM-DD), para los valores por defecto de las fechas. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
